import {
  HistoryIcon,
  PowerIcon,
  PowerOffIcon,
  RouteIcon,
  ServerOffIcon,
  ShuffleIcon,
  Trash2Icon,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useConfigEvents } from "@/services/admin/processor-config"
import type {
  ConfigurationEvent,
  ConfigurationEventType,
} from "@/types/admin-processor-config"

const prefix = "pages.adminProcessorConfig.trail"

/**
 * Cor por tipo de evento — semântica, não decorativa: ligar é sucesso, desligar e apagar
 * são perda de roteamento, trocar é mudança de rota.
 */
const TONE: Record<
  string,
  { badge: string; icon: typeof PowerIcon }
> = {
  ACTIVATED: {
    badge:
      "border-emerald-200 bg-emerald-50 text-emerald-700",
    icon: PowerIcon,
  },
  DEACTIVATED: {
    badge: "border-amber-200 bg-amber-50 text-amber-700",
    icon: PowerOffIcon,
  },
  PROCESSOR_CHANGED: {
    badge: "border-blue-200 bg-blue-50 text-blue-700",
    icon: ShuffleIcon,
  },
  // Violeta, e não o azul da troca de processador: as duas mexem em credencial, mas
  // só uma muda por onde o dinheiro sai. Cores iguais fariam a trilha parecer
  // registrar a mesma coisa duas vezes quando `moveFallback` gera os dois eventos.
  FALLBACK_CHANGED: {
    badge: "border-violet-200 bg-violet-50 text-violet-700",
    icon: RouteIcon,
  },
  DELETED: {
    badge: "border-red-200 bg-red-50 text-red-700",
    icon: Trash2Icon,
  },
}

const FALLBACK_TONE = {
  badge: "border-gray-200 bg-gray-50 text-gray-700",
  icon: HistoryIcon,
}

const toneOf = (eventType: ConfigurationEventType) =>
  TONE[eventType] ?? FALLBACK_TONE

/** Eventos que dizem "de X para Y" — os outros só têm um estado depois. */
const SLOT_CHANGES = [
  "PROCESSOR_CHANGED",
  "FALLBACK_CHANGED",
]

const isSlotChange = (eventType: ConfigurationEventType) =>
  SLOT_CHANGES.includes(eventType)

/**
 * Qual credencial o evento mexeu.
 *
 * O campo é recente: eventos gravados antes dele vêm sem `slot`, e todos eram do processador
 * principal — o único que existia para trocar. Assumir `PRINCIPAL` na ausência mantém a trilha
 * antiga legível em vez de mostrar um rótulo vazio.
 */
const slotOf = (event: ConfigurationEvent) =>
  event.slot ??
  (event.eventType === "FALLBACK_CHANGED"
    ? "FALLBACK"
    : "PRINCIPAL")

/**
 * O backend manda `yyyy-MM-dd'T'HH:mm:ss` sem fuso — é hora local do servidor.
 * Interpretar como UTC deslocaria o horário; o `Date` do JS já trata string sem
 * offset como local, e o Intl só cuida do formato do locale do operador.
 */
const formatEventDate = (value: string, locale: string) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date)
}

const TrailRow = ({
  event,
}: {
  event: ConfigurationEvent
}) => {
  const { t, i18n } = useTranslation()
  const tone = toneOf(event.eventType)
  const Icon = tone.icon

  return (
    <li
      data-testid={`trail-event-${event.id}`}
      className="flex gap-3 py-3"
    >
      <span
        aria-hidden="true"
        className={`flex size-8 shrink-0 items-center justify-center rounded-full border ${tone.badge}`}
      >
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant="outline"
            className={`font-normal ${tone.badge}`}
          >
            {t(`${prefix}.types.${event.eventType}`, {
              defaultValue: event.eventType,
            })}
          </Badge>
          {event.createdAt && (
            <time
              dateTime={event.createdAt}
              className="text-xs tabular-nums text-muted-foreground"
            >
              {formatEventDate(
                event.createdAt,
                i18n.language
              )}
            </time>
          )}
        </div>
        {isSlotChange(event.eventType) && (
          <p className="break-words text-xs text-muted-foreground">
            {t(`${prefix}.movedSlot`, {
              slot: t(`${prefix}.slots.${slotOf(event)}`, {
                defaultValue: slotOf(event),
              }),
              from:
                event.fromProcessorId ??
                t(`${prefix}.unknownProcessor`),
              to:
                event.toProcessorId ??
                t(`${prefix}.unknownProcessor`),
            })}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          {event.userId
            ? t(`${prefix}.byUser`, { user: event.userId })
            : t(`${prefix}.byAutomation`)}
        </p>
      </div>
    </li>
  )
}

/**
 * Trilha do meio de pagamento: quem mudou por onde o pagamento sai.
 *
 * Trocar de credencial, ligar ou desligar são decisões com consequência financeira. Antes
 * disto o painel mostrava o estado, mas nunca quem o produziu — e a pergunta "quem mudou
 * isso?" só tinha resposta no banco.
 */
export const ConfigEventsTrail = ({
  configId,
}: {
  configId: string
}) => {
  const { t } = useTranslation()
  const { data, isLoading, isError, refetch } =
    useConfigEvents(configId)

  const events = data?.content ?? []

  return (
    <section className="rounded-xl border bg-card p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-sm font-medium">
          {t(`${prefix}.title`)}
        </h2>
        {/* O badge diz o TOTAL, mas a lista mostra a primeira página: sem o
            rótulo, 45 eventos com 20 na tela pareceriam trilha truncada em
            silêncio. */}
        {data && data.totalElements > 0 && (
          <Badge
            variant="secondary"
            className="tabular-nums"
          >
            {data.totalElements > events.length
              ? t(`${prefix}.showingLatest`, {
                  shown: events.length,
                  total: data.totalElements,
                })
              : data.totalElements}
          </Badge>
        )}
      </div>

      {isLoading && (
        <div
          data-testid="trail-skeleton"
          className="space-y-3"
        >
          {Array.from({ length: 3 }).map((_, row) => (
            <Skeleton
              key={`trail-skeleton-${row}`}
              className="h-12 w-full"
            />
          ))}
        </div>
      )}

      {/* A trilha é acessória da página: falhar nela não pode esconder a
          configuração, que é o que o operador veio ver. */}
      {!isLoading && isError && (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed p-6 text-center">
          <ServerOffIcon
            className="size-6 text-muted-foreground"
            aria-hidden="true"
          />
          <p className="text-xs text-muted-foreground">
            {t(`${prefix}.loadError`)}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => refetch()}
          >
            {t(`${prefix}.retry`)}
          </Button>
        </div>
      )}

      {!isLoading && !isError && events.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed p-6 text-center">
          <HistoryIcon
            className="size-6 text-muted-foreground"
            aria-hidden="true"
          />
          <p className="text-xs text-muted-foreground">
            {t(`${prefix}.empty`)}
          </p>
        </div>
      )}

      {!isLoading && !isError && events.length > 0 && (
        <ul className="divide-y">
          {events.map((event) => (
            <TrailRow key={event.id} event={event} />
          ))}
        </ul>
      )}
    </section>
  )
}
